import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { OuterTabGroup } from '../../components/form/tab-groups'
import { useI18N } from '../../../libraries/i18n/hook'
import useCategories from '../../../hocs/content/useCategories'
import * as contentLib from '../../../libraries/content'
import ErrorMessage from '../../components/errors/ErrorMessage'
import { Video } from '../../../api/videos/types'
import SortingBar from '../../components/grid/SortingBar'
import VideoGrid from '../../components/content/VideoGrid'
import Loading from '../../components/layout/Loading'
import DocumentGrid from '../../components/content/DocumentGrid'
import { Document } from '../../../api/documents/types'
import ContentFilters from './ContentFilters'
import { useAllVideos } from '../../../hocs/content/useVideos'
import { useAllDocuments } from '../../../hocs/content/useDocuments'
import PageHeading from '../../components/layout/PageHeading'
import { useContentFilter } from '../../../libraries/search/filters'
import NoResults from '../../components/layout/NoResults'

const VIDEO_TAB_ID = 'videos'
const DOCUMENT_TAB_ID = 'documents'

const ContentExplorer = () => {
  const { t } = useI18N()
  const location = useLocation()
  const navigate = useNavigate()
  const [urlSearchParams] = React.useState(new URLSearchParams(location.search))
  const { filters, setFilters } = useContentFilter()
  const { data: allCategories, isLoading: allCategoriesLoading } = useCategories()
  const { data: allDocs, isLoading: allDocsLoading, error: allDocsError } = useAllDocuments()
  const { data: allVideos, isLoading: allVideosLoading, error: allVideosError } = useAllVideos()
  React.useEffect(() => {
    const mainCategory = urlSearchParams.get('mainCategory')
    if(mainCategory) {
      setFilters({ mainCategory })
    }
  }, [])
  if (allDocsError) {
    return <ErrorMessage message={allDocsError} />
  }

  const getSortBy = () => filters.sortBy || contentLib.SORTING_OPTIONS.RECOMMENDED
  const handleClearFilters = () => {
    setFilters({}, true)
  }

  const getSortingOptions = () => {
    return [
      { label: t('common.sortBy.recommended'), value: contentLib.SORTING_OPTIONS.RECOMMENDED },
      { label: t('common.sortBy.latest'), value: contentLib.SORTING_OPTIONS.LATEST },
      { label: t('common.sortBy.title'), value: contentLib.SORTING_OPTIONS.ALPHABETIC }
    ]
  }

  const renderVideosExplorer = () => {
    const sortingOptions = getSortingOptions()
    if (allVideosLoading) { return <Loading size='fill-container' /> }
    const filteredVideos = contentLib.filterContent<Video>(allVideos, filters)
    if ((filteredVideos?.length ?? 0) === 0) {
      return (
        <>
          <ContentFilters filters={filters} onChange={setFilters} contentType='video' />
          <NoResults onClearFilter={handleClearFilters} totalResults={allVideos?.length ?? 0} />
        </>
      )
    }
    return (
      <>
        <ContentFilters filters={filters} onChange={setFilters} contentType='video' />
        <div className='mb-2' />
        <SortingBar handleChange={setFilters} itemsAmount={filteredVideos?.length || 0} options={sortingOptions} currentValue={getSortBy()} />
        <VideoGrid data={contentLib.sortContent<Video>(filteredVideos, getSortBy(), 'title')} isLoading={allVideosLoading} error={allVideosError} />
      </>
    )
  }

  const renderDocumentsExplorer = () => {
    if (allDocsLoading) { return <Loading size='fill-container' /> }
    const sortingOptions = getSortingOptions()
    const filteredDocs = contentLib.filterContent<Document>(allDocs, filters)
    if ((filteredDocs?.length ?? 0) === 0) {
      return (
        <>
          <ContentFilters filters={filters} onChange={setFilters} contentType='document' />
          <NoResults onClearFilter={handleClearFilters} totalResults={allDocs?.length ?? 0} />
        </>
      )
    }
    return (
      <>
        <ContentFilters filters={filters} onChange={setFilters} contentType='document' />
        <div className='mb-2' />
        <SortingBar handleChange={setFilters} itemsAmount={allDocs?.length || 0} options={sortingOptions} currentValue={getSortBy()} />
        <DocumentGrid data={contentLib.sortContent<Document>(filteredDocs, getSortBy(), 'title')} isLoading={allDocsLoading} error={allDocsError} />
      </>
    )
  }

  const getContentCategoryName = () => {
    if (allCategoriesLoading) return ''
    const category = allCategories?.find((cat) => cat.id?.toString() === filters.mainCategory?.toString())
    return category?.name || t('content.categories.all')
  }

  const activeTab = urlSearchParams.get('tab') || VIDEO_TAB_ID
  return (
    <div>
      <PageHeading>{getContentCategoryName()}</PageHeading>
      <OuterTabGroup
        tabs={[{ id: VIDEO_TAB_ID, title: t('content.videos') }, { id: DOCUMENT_TAB_ID, title: t('content.documents') }]}
        handleClick={(tabId: string) => {
          const newParams: Record<string, string> = { tab: tabId }
          navigate({ search: new URLSearchParams(newParams).toString() })
        }}
        active={activeTab}
      />
      {activeTab === VIDEO_TAB_ID && renderVideosExplorer()}
      {activeTab === DOCUMENT_TAB_ID && renderDocumentsExplorer()}
    </div>
  )
}

export default ContentExplorer
